/**
 * Shared invoice contract. The backend's Mongoose schema and the OCR service's
 * pydantic models reproduce the same shape — keep them in lock-step.
 * Monetary values are integers in MINOR units (cents).
 */

export type InvoiceStatus = 'processing' | 'ready' | 'failed' | 'edited' | 'draft';

export interface Party {
  name: string;
  address: string;
  taxId: string;
  email: string;
  phone: string;
}

export interface LineItem {
  description: string;
  quantity: number;
  unitPrice: number;        // minor units
  taxRate: number;          // basis points, e.g. 2000 = 20%
  discountBp?: number;      // basis points, e.g. 1000 = 10% off this line
  total: number;            // minor units (after discount, before tax)
}

export interface InvoiceSource {
  filename: string;
  mimeType: string;
  size: number;
  uploadedAt: string;
  checksumSha256: string;
}

export type VerificationSeverity = 'high' | 'medium' | 'low';

export interface VerificationIssue {
  field: string;
  issue: string;
  severity: VerificationSeverity;
}

export interface VerificationResult {
  issues: VerificationIssue[];
  verifiedAt: string | null;
  model: string;
}

export interface InvoiceRaw {
  ocrText: string;
  confidence: number;
  extractor: string;
  processedAt: string;
  verification?: VerificationResult;
}

export type InvoiceOrigin = 'uploaded' | 'created';

export interface Invoice {
  id: string;
  ownerId: string;
  status: InvoiceStatus;
  origin: InvoiceOrigin;
  /** When false, the invoice appears in the main Invoices tab.
   *  When true, it lives in Archive. User-created invoices default to false,
   *  uploaded scans default to true. The user can move either way. */
  archived: boolean;
  theme: string;
  accentColor: string;
  clientId: string | null;
  source: InvoiceSource;
  vendor: Party;
  buyer: Party;
  invoiceNumber: string;
  issueDate: string;
  dueDate: string | null;
  currency: string;
  lineItems: LineItem[];
  subtotal: number;
  taxTotal: number;
  total: number;
  paymentTerms: string | null;
  notes: string | null;
  raw: InvoiceRaw;
  createdAt: string;
  updatedAt: string;
}

export type ExportFormat = 'json' | 'csv' | 'xlsx' | 'pdf' | 'xml' | 'ubl' | 'facturx' | 'txt';

export const EXPORT_FORMATS: { key: ExportFormat; label: string; desc: string }[] = [
  { key: 'json',    label: 'JSON',     desc: 'Pretty-printed JSON.' },
  { key: 'csv',     label: 'CSV',      desc: 'One line item per row, header included.' },
  { key: 'xlsx',    label: 'XLSX',     desc: 'Spreadsheet with header + items + totals sheet.' },
  { key: 'pdf',     label: 'PDF',      desc: 'Reformatted invoice PDF.' },
  { key: 'xml',     label: 'XML',      desc: 'Plain XML mirror of the structured fields.' },
  { key: 'ubl',     label: 'UBL 2.1',  desc: 'XML conforming to UBL 2.1.' },
  { key: 'facturx', label: 'Factur-X', desc: 'PDF/A-3 with embedded UBL XML.' },
  { key: 'txt',     label: 'TXT',      desc: 'Plain-text raw OCR + summary.' },
];

export const SUPPORTED_MIME: Record<string, string> = {
  'application/pdf': 'PDF',
  'image/png': 'PNG',
  'image/jpeg': 'JPG',
  'image/tiff': 'TIFF',
  'image/bmp': 'BMP',
  'image/heic': 'HEIC',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'application/msword': 'DOC',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'application/vnd.ms-excel': 'XLS',
};

export const MAX_FILE_BYTES = 25 * 1024 * 1024;
export const MAX_FILES_PER_UPLOAD = 10;
